import { useEffect, useRef, useState, type FormEvent } from "react";

import { createManualContact, type ManualContactPayload } from "@/api/crm";
import type { CrmContact } from "@/types/crm";
import { useToast } from "@/components/ToastProvider";

const CONTACT_TYPES = ["Person", "Organization", "Venue", "Media", "Other"];
const ROLE_OPTIONS = ["BOOKER", "OWNER", "MANAGER", "PROMOTER", "ARTIST", "OTHER"];
const STAGE_OPTIONS = ["New", "Engaged", "Qualified", "Dormant"];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const createDefaultForm = () => ({
  display_name: "",
  company_name: "",
  contact_type: "Person",
  role_id: "BOOKER",
  crm_stage: "New",
  email: "",
  phone: "",
  url: "",
  address_line: "",
  location_city: "",
  location_region: "",
  country: "",
  owner: "",
  tags: "",
  source_url: "",
  notes: "",
});

type ManualContactCardProps = {
  onSaved?: (contact?: CrmContact) => void;
  initialValues?: Partial<ReturnType<typeof createDefaultForm>>;
};

export default function ManualContactCard({ onSaved, initialValues }: ManualContactCardProps) {
  const [form, setForm] = useState(createDefaultForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { push } = useToast();
  const lastSeedRef = useRef<string>("");

  useEffect(() => {
    if (!initialValues) return;
    const seed = JSON.stringify(initialValues);
    if (seed === lastSeedRef.current) return;
    lastSeedRef.current = seed;
    const sanitized = Object.fromEntries(
      Object.entries(initialValues).filter(([, value]) => value !== undefined),
    ) as Partial<ReturnType<typeof createDefaultForm>>;
    setForm((prev) => ({
      ...createDefaultForm(),
      ...sanitized,
      email: initialValues.email ?? prev.email,
    }));
  }, [initialValues]);

  const canSave = Boolean((form.display_name ?? "").trim() || (form.company_name ?? "").trim());

  const handleFieldChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSave) {
      setError("Add a name or company before saving.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await createManualContact({
        display_name: form.display_name || undefined,
        company_name: form.company_name || undefined,
        contact_type: form.contact_type as ManualContactPayload["contact_type"],
        role_id: form.role_id,
        crm_stage: form.crm_stage || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        url: form.url || undefined,
        address: form.address_line || undefined,
        city: form.location_city || undefined,
        state: form.location_region || undefined,
        country: form.country || undefined,
        owner: form.owner || undefined,
        tags: form.tags
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
        source_url: form.source_url || undefined,
        notes: form.notes || undefined,
      });
      push({ title: "Contact saved", message: form.display_name || form.company_name || "New contact" });
      setForm(createDefaultForm());
      onSaved?.(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save contact.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm(createDefaultForm());
    setError(null);
  };

  return (
    <section className="space-y-4 rounded-2xl border border-border/60 bg-background/70 p-6 shadow-inner shadow-black/30">
      <header>
        <p className="text-xs uppercase tracking-wide text-primary">Manual contact entry</p>
        <h3 className="text-xl font-semibold text-foreground">Create a CRM contact</h3>
        <p className="text-sm text-muted-foreground/80">
          Add a hand-curated contact that will be stored in Chroma and linked to other memory systems.
        </p>
      </header>
      <form className="grid gap-3" onSubmit={handleSubmit}>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            Display name
            <input
              value={form.display_name}
              onChange={(event) => handleFieldChange("display_name", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
              placeholder="Jane Booker"
            />
          </label>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            Company
            <input
              value={form.company_name}
              onChange={(event) => handleFieldChange("company_name", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
              placeholder="River Bend Venue"
            />
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            Contact type
            <select
              value={form.contact_type}
              onChange={(event) => handleFieldChange("contact_type", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
            >
              {CONTACT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            Role
            <select
              value={form.role_id}
              onChange={(event) => handleFieldChange("role_id", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
            >
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            Stage
            <select
              value={form.crm_stage}
              onChange={(event) => handleFieldChange("crm_stage", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
            >
              {STAGE_OPTIONS.map((stage) => (
                <option key={stage} value={stage}>
                  {stage}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            Email
            <input
              value={form.email}
              onChange={(event) => handleFieldChange("email", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
              placeholder="jane@example.com"
            />
          </label>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            Phone
            <input
              value={form.phone}
              onChange={(event) => handleFieldChange("phone", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
              placeholder="+1 (555) 123-4567"
            />
          </label>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            URL
            <input
              value={form.url}
              onChange={(event) => handleFieldChange("url", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
              placeholder="https://example.com"
            />
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            Address
            <input
              value={form.address_line}
              onChange={(event) => handleFieldChange("address_line", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
              placeholder="123 Street"
            />
          </label>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            City
            <input
              value={form.location_city}
              onChange={(event) => handleFieldChange("location_city", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
              placeholder="CityName"
            />
          </label>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            State
            <select
              value={form.location_region}
              onChange={(event) => handleFieldChange("location_region", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
            >
              <option value="">Select state</option>
              {US_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            Country
            <input
              value={form.country}
              onChange={(event) => handleFieldChange("country", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
              placeholder="USA"
            />
          </label>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            Contact Name
            <input
              value={form.owner}
              onChange={(event) => handleFieldChange("owner", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
              placeholder="Team lead"
            />
          </label>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
            Tags
            <input
              value={form.tags}
              onChange={(event) => handleFieldChange("tags", event.target.value)}
              className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
              placeholder="priority, tour-staff"
            />
          </label>
        </div>
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
          Source URL
          <input
            value={form.source_url}
            onChange={(event) => handleFieldChange("source_url", event.target.value)}
            className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
            placeholder="https://memory.example.com/venue"
          />
        </label>
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
          Notes
          <textarea
            value={form.notes}
            onChange={(event) => handleFieldChange("notes", event.target.value)}
            className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-foreground"
            rows={3}
            placeholder="Add a summary for memory and dedup"
          />
        </label>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={saving || !canSave}
            className="rounded-full border border-gold/60 bg-gold/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gold transition hover:border-gold/40 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save contact"}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80 hover:text-foreground"
          >
            Reset
          </button>
        </div>
      </form>
    </section>
  )
}
